import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '~/components/atom/Icon';
import JustificationContainer from '~/components/atom/JustificationContainer';
import type { ThemeColor } from '~/theme/System/tokens/colorPalette';

const DEFAULT_MAX_WIDTH = '350px';

type Props = {
  src: string;
  maxWidth?: string;
  backgroundColor?: ThemeColor;
};

const AudioPlayer: React.FC<Props> = ({
  src,
  maxWidth = DEFAULT_MAX_WIDTH,
  backgroundColor = { group: 'primary', variant: 'translucent' },
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1); // Default volume: 100%
  const [previousVolume, setPreviousVolume] = useState(1); // To store the previous volume before muting

  const togglePlay = async () => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      await audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setProgress(time);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const volumeLevel = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.volume = volumeLevel;
    }
    setVolume(volumeLevel);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    if (audioRef.current.volume > 0) {
      setPreviousVolume(audioRef.current.volume);
      audioRef.current.volume = 0;
      setVolume(0);
    } else {
      audioRef.current.volume = previousVolume;
      setVolume(previousVolume);
    }
  };

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Container
      align="center"
      justification="center"
      width="100%"
      padding={['s', 'base']}
      border={{ radius: 'base' }}
      backgroundColor={backgroundColor}
      $maxWidth={maxWidth}
    >
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
      />
      <JustificationContainer align="center" width="100%">
        <PlayButton onClick={togglePlay}>
          <Icon name={isPlaying ? 'pause' : 'play'} size="m" />
        </PlayButton>
        <Time>
          {formatTime(progress)} / {formatTime(duration)}
        </Time>
        <ProgressBar
          type="range"
          min={0}
          max={duration}
          step="0.1"
          value={progress}
          onChange={handleSeek}
        />
        <VolumeControl>
          <Icon
            size="m"
            name={volume > 0 ? 'volume-2' : 'volume-x'}
            onClick={toggleMute}
          />
          <VolumeSlider
            type="range"
            min={0}
            max={1}
            step="0.01"
            value={volume}
            $value={volume}
            onChange={handleVolumeChange}
          />
        </VolumeControl>
      </JustificationContainer>
    </Container>
  );
};

const Container = styled(JustificationContainer)<{ $maxWidth: string }>(
  ({ $maxWidth }) => css`
    max-width: ${$maxWidth};
  `,
);

const PlayButton = styled.button(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: ${theme.color('primary', 'dark')};
    margin-right: ${theme.space('xxs')};
    cursor: pointer;
  `,
);

const Time = styled.span(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    color: ${theme.color('primary', 'dark')};
    font-size: ${theme.fontSize('base')};
    margin-right: ${theme.space('xxs')};
    margin-top: 1px;
    width: 80px;
  `,
);

const VolumeControl = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    margin-right: ${theme.space('s')};

    &:hover input[type='range'] {
      visibility: visible;
      opacity: 1;
    }
  `,
);

const VolumeSlider = styled.input<{ $value: number }>(
  ({ theme, $value }) => css`
    background-color: ${theme.color('tertiary', 'light')};
    padding: ${theme.space('xs')};
    border-radius: ${theme.getTokens().border.radius.s};
    position: absolute;
    cursor: pointer;
    top: -50px;
    transform: rotate(-90deg);
    width: 80px;
    visibility: hidden;
    opacity: 0;
    transition:
      opacity 0.3s ease,
      visibility 0.3s ease;
    appearance: none;

    &::-webkit-slider-runnable-track {
      height: 4px;
      background: linear-gradient(
        to right,
        ${theme.color('secondary')} 0%,
        ${theme.color('secondary')} ${$value * 100}%,
        ${theme.color('tertiary')} ${$value * 100}%,
        ${theme.color('tertiary')} 100%
      );
      border-radius: ${theme.getTokens().border.radius.s};
    }

    &::-webkit-slider-thumb {
      appearance: none;
      height: 12px;
      width: 12px;
      border-radius: 50%;
      background: ${theme.color('secondary')};
      cursor: pointer;
      margin-top: -4px;
    }

    &::-moz-range-track {
      height: 4px;
      background: ${theme.color('tertiary')};
      border-radius: 2px;
    }

    &::-moz-range-progress {
      background: ${theme.color('secondary')};
      height: 4px;
      border-radius: 2px;
    }

    &::-moz-range-thumb {
      height: 12px;
      width: 12px;
      border-radius: 50%;
      background: ${theme.color('secondary')};
      cursor: pointer;
    }

    &::-ms-track {
      height: 4px;
      background: ${theme.color('tertiary')};
      border-radius: 2px;
      border-color: transparent;
      color: transparent;
    }

    &::-ms-fill-lower {
      background: ${theme.color('secondary')};
      height: 4px;
      border-radius: 2px;
    }

    &::-ms-thumb {
      height: 12px;
      width: 12px;
      border-radius: 50%;
      background: ${theme.color('secondary')};
      cursor: pointer;
    }
  `,
);

const ProgressBar = styled.input(
  ({ theme }) => css`
    flex: 1;
    margin-right: ${theme.space('s')};
    height: 2px;
    accent-color: ${theme.color('secondary', 'dark')};
    cursor: pointer;
  `,
);

export default AudioPlayer;
